import { Delete, Phone } from 'lucide-react';

const KEYS = [
  { digit: '1', sub: '' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: '*', sub: '' },
  { digit: '0', sub: '+' },
  { digit: '#', sub: '' },
];

// Normalize to E.164
function normalizePhone(raw) {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.startsWith('+') ? raw : `+${digits}`;
}

export default function DialPad({ number, setNumber, onCall, disabled = false }) {
  const handleKey = (digit) => {
    setNumber(n => n + digit);
  };

  const handleDelete = () => {
    setNumber(n => n.slice(0, -1));
  };

  const handleCall = () => {
    if (!number.trim()) return;
    onCall(normalizePhone(number.trim()), number.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCall();
    if (e.key === 'Backspace' && !e.target.value) handleDelete();
  };

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-[260px] mx-auto">
      {/* Number Display */}
      <div className="relative w-full">
        <input
          data-testid="phone-dialpad-input"
          type="tel"
          value={number}
          onChange={e => setNumber(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter number..."
          className="
            w-full text-center text-2xl font-light tracking-widest
            bg-slate-50 border border-slate-200 rounded-2xl
            px-4 py-3 pr-10 text-slate-800
            focus:outline-none focus:border-[#003366]/40 focus:ring-2 focus:ring-[#003366]/10
            placeholder:text-slate-300 placeholder:text-lg placeholder:font-light
            transition-all
          "
        />
        {number && (
          <button
            onClick={handleDelete}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
          >
            <Delete className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Keypad Grid */}
      <div className="grid grid-cols-3 gap-2.5 w-full">
        {KEYS.map(({ digit, sub }) => (
          <button
            key={digit}
            data-testid={`dialpad-key-${digit}`}
            onClick={() => handleKey(digit)}
            className="
              flex flex-col items-center justify-center
              h-14 rounded-2xl bg-white border border-slate-200
              hover:bg-slate-50 hover:border-slate-300
              active:scale-95 active:bg-slate-100
              transition-all duration-100 shadow-sm
              select-none
            "
          >
            <span className="text-xl font-medium text-slate-800 leading-tight">{digit}</span>
            {sub && <span className="text-[9px] font-semibold text-slate-400 tracking-widest mt-0.5">{sub}</span>}
          </button>
        ))}
      </div>

      {/* Call Button */}
      <button
        data-testid="dialpad-call-btn"
        onClick={handleCall}
        disabled={!number.trim() || disabled}
        className="
          w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600
          disabled:bg-slate-200 disabled:cursor-not-allowed
          flex items-center justify-center
          shadow-lg shadow-emerald-200
          active:scale-95 transition-all duration-150
          mt-1
        "
      >
        <Phone className="w-7 h-7 text-white" strokeWidth={2} />
      </button>
    </div>
  );
}
